import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Assessment } from '@libs/types';
import { Box, Button, Chip, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useGetAllAssessments } from '@features/assessment';
import { useDirection } from '@hooks';
import { AssessmentStatus, LimitPerPage, Order } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { ArrowRightIcon, PencilSimpleIcon, PlusIcon, SpinnerGapIcon } from '@phosphor-icons/react';

import { DashboardWidget } from './DashboardWidget';

const listParams = (status: AssessmentStatus) => ({
  status,
  sortBy: 'updatedAt' as const,
  order: Order.DESC,
  page: 1,
  limit: LimitPerPage.TEN,
});

const ProcessingCard: FC<{ run: Assessment }> = observer(({ run }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const progress = Math.min(100, Math.max(0, run.progress ?? 0));
  // Not clickable while processing: the assessment isn't ready to open yet.
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 3,
        p: 1.75,
        cursor: 'default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Chip
          label={t('app.dashboard.activeWork.processing')}
          size="small"
          sx={{
            height: 22,
            fontSize: 11.5,
            fontWeight: 500,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.blue[700],
          }}
        />
        <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 500 }} noWrap>
          {run.title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 999,
            backgroundColor: theme.palette.grey[300],
            '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.blue[700], borderRadius: 999 },
          }}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.palette.blue[700] }}>
          {progress}%
        </Typography>
      </Box>
    </Box>
  );
});

const DraftCard: FC<{ run: Assessment }> = observer(({ run }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const progress = Math.min(100, Math.max(0, run.progress ?? 0));
  return (
    <Box sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 3, p: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <Chip
          label={t('app.dashboard.activeWork.draft')}
          size="small"
          sx={{
            height: 22,
            fontSize: 11.5,
            fontWeight: 500,
            color: theme.palette.common.white,
            backgroundColor: theme.palette.grey[500],
          }}
        />
        <Typography sx={{ flex: 1, fontSize: 12.5, fontWeight: 500 }} noWrap>
          {run.title}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 999,
          mb: 1.5,
          backgroundColor: theme.palette.grey[300],
          '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.grey[500], borderRadius: 999 },
        }}
      />
      <Button
        fullWidth
        variant="secondary"
        startIcon={<PencilSimpleIcon size={14} />}
        onClick={() => navigate(routes.assessmentDomain(run.id, run.currentDomainIndex))}
        sx={{ textTransform: 'none' }}
      >
        {t('app.dashboard.activeWork.resumeDraft')}
      </Button>
    </Box>
  );
});

/** In-flight assessments (processing + drafts) with a launch CTA. */
export const ActiveWorkspace: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const { isRtl } = useDirection();

  const { data: processing } = useGetAllAssessments(listParams(AssessmentStatus.PROCESSING));
  const { data: drafts } = useGetAllAssessments(listParams(AssessmentStatus.DRAFT));

  const processingRows = useMemo(() => processing?.rows ?? [], [processing]);
  const draftRows = useMemo(() => drafts?.rows ?? [], [drafts]);
  const isEmpty = processingRows.length === 0 && draftRows.length === 0;

  // Show at most 3 items in the widget (processing prioritised); the rest live on the assessments page.
  const MAX_VISIBLE = 3;
  const visibleProcessing = processingRows.slice(0, MAX_VISIBLE);
  const visibleDrafts = draftRows.slice(0, Math.max(0, MAX_VISIBLE - visibleProcessing.length));
  const hiddenCount =
    processingRows.length + draftRows.length - visibleProcessing.length - visibleDrafts.length;

  return (
    <DashboardWidget
      icon={<SpinnerGapIcon size={19} />}
      iconColor={theme.palette.blue[700]}
      title={t('app.dashboard.activeWork.title')}
      subtitle={t('app.dashboard.activeWork.subtitle')}
      right={
        <Button
          variant="text"
          size="small"
          endIcon={<ArrowRightIcon size={14} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
          onClick={() => navigate(routes.assessmentList())}
          sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}
        >
          {t('app.dashboard.activeWork.viewAll')}
        </Button>
      }
    >
      <Box sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'auto' }}>
        {visibleProcessing.map((run) => (
          <ProcessingCard key={run.id} run={run} />
        ))}
        {visibleDrafts.map((run) => (
          <DraftCard key={run.id} run={run} />
        ))}
        {isEmpty && (
          <Typography sx={{ fontSize: 13, color: 'text.tertiary', textAlign: 'center', py: 2 }}>
            {t('app.dashboard.activeWork.empty')}
          </Typography>
        )}
        {hiddenCount > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={() => navigate(routes.assessmentList())}
            sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, alignSelf: 'center' }}
          >
            {t('app.dashboard.activeWork.loadMore', { count: hiddenCount })}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          fullWidth
          variant="contained"
          startIcon={<PlusIcon size={16} />}
          onClick={() => navigate(routes.assessmentNew())}
          sx={{ textTransform: 'none' }}
        >
          {t('app.dashboard.activeWork.launch')}
        </Button>
      </Box>
    </DashboardWidget>
  );
});
